import { Injectable, Injector, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { SentryErrorHandler } from '@sentry/angular';
import { ConfirmationService } from 'primeng/api';
import { UtilDialogService } from './dialog.service';

@Injectable()
export class GlobalErrorHandler extends SentryErrorHandler {
  lastPrompt = new Date(2000, 0, 1);
  constructor(
    private router: Router,
    private dialog: ConfirmationService,
    private zone: NgZone,
    // Resolved lazily inside handleError — UtilDialogService pulls a heavy
    // dependency chain (HttpClient, DialogService, ...) and ErrorHandler is
    // instantiated very early in bootstrap, so injecting it in the constructor
    // risks a DI cycle / bootstrap hang.
    private injector: Injector,
  ) {
    super();
  }

  override handleError(error: any) {
    // Every deploy or app-pool restart signs everyone out (401). MyInterceptor already redirects
    // to login, so this is not a code error: skip the Sentry report.
    if (this.isSignOut401(error)) {
      console.warn('Signed out by the server (401); the login page is shown.', error);
      return;
    }

    super.handleError(error);
    if (error.rejection) {
      // extract error from promise rejection
      error = error.rejection;
    }

    console.error(error);

    // These statuses are already surfaced to the user by MyInterceptor (toast +,
    // for 401, a redirect to login). Don't double-handle them with a dialog.
    if (error.status == 401 || error.status == 0 || error.status == 500) {
      return;
    }

    // A server save rejection or other HTTP error is recoverable — show it in a
    // proper in-app dialog. The native window.confirm fallback below is reserved
    // for catastrophic JS errors (e.g. TypeError) that can break Angular itself.
    if (this.isRecoverableServerError(error)) {
      // Throttle so an error storm (e.g. a retry loop) can't stack dialogs.
      const now = new Date();
      if (now.getTime() - this.lastPrompt.getTime() <= 10000) {
        return;
      }
      this.lastPrompt = now;
      try {
        const dialogService = this.injector.get(UtilDialogService);
        this.zone.run(() => dialogService.saveErrorDialog(error));
      } catch {
        // Resolving/opening the dialog failed — fall back to the native prompt
        // rather than letting handleError itself throw.
        this.showConfirmErrorDialog(error);
      }
      return;
    }

    this.showConfirmErrorDialog(error);
  }

  /** True for a 401 that MyInterceptor turned into a redirect to login. Raw HttpErrorResponse
   * and Breeze errors both carry `status`; Breeze also keeps it on `httpResponse`. A 401 from a
   * /Login/ url is excluded: MyInterceptor does not redirect for those, so it is still reported. */
  private isSignOut401(error: any): boolean {
    const e = error?.rejection ?? error;
    if (e == null || (e.status ?? e.httpResponse?.status) !== 401) return false;
    const url: string = e.url ?? e.httpResponse?.config?.url ?? '';
    return !url.includes('/Login/');
  }

  /** True for HTTP responses and Breeze save errors — handled with an in-app dialog. */
  private isRecoverableServerError(error: any): boolean {
    return error != null && (typeof error.status === 'number' || error.entityErrors != null);
  }

  /** Special handling for TypeError, which can disable Angular change detection and/or cause infinite loops */
  private showConfirmErrorDialog(error: any) {
    const now = new Date();
    if (now.getTime() - this.lastPrompt.getTime() > 10000) {
      this.lastPrompt = now;
      const nav = window.confirm(
        'An error has occurred, which may cause problems on the current page:\n\n' +
          error.message +
          '\n\nClick OK to return to the home page.',
      );
      this.lastPrompt = new Date();
      if (nav) {
        // Ok
        window.location.hash = '';
        window.location.reload();
      } else {
        // Cancel
        // window.location.reload();
      }
    }
  }
}
